import EventCard from '../components/EventCard';
import OfflinePill from '../components/OfflinePill';
import Wardrobe from '../components/Wardrobe';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import WeatherHours from '../components/WeatherHours';
import WeatherRail from '../components/WeatherRail';
import { useWeather } from '../lib/weather';
import { useIsDesktop } from '../lib/viewport';
import { MarkWhistle } from '../components/Icon';

/**
 * Artboards 1d (phone) and 1l (laptop).
 * Same components either way: on a phone the days stack under sticky headers;
 * on a laptop the planner and the newest photos move into a second column and
 * the event rows grow a time gutter. Nothing is added or removed between them.
 */
export default function Schedule() {
  const model = useScheduleModel();
  const desktop = useIsDesktop();
  /* Before the two early returns below, so a failed schedule fetch does not
     also cancel the weather. */
  const weather = useWeather();

  if (model.loading) {
    return (
      <div className="page">
        <div className="day-head"><h4>Loading the week…</h4></div>
        <SkeletonEvents />
      </div>
    );
  }

  if (model.error && model.events.length === 0) {
    return (
      <div className="page empty">
        <MarkWhistle className="mark" size={44} />
        <h4 style={{ margin: 0 }}>The schedule didn't load</h4>
        <p>The connection dropped before we got it. It is only a small file — try once more.</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const days = (
    <>
      {model.days.map((day) => (
        <section key={day.key}>
          <div className="day-head">
            <h4>{desktop ? day.longLabel : day.label}</h4>
            {day.isToday && <span className="day-note">Today</span>}
          </div>
          {/* **Phone only.** On a laptop the rail carries `WeatherRail`, which
              says the same thing with room to spell the conditions out, and a
              strip here as well would be the same forecast twice. There is no
              rail at this width — `.cols` collapses — so the strip is how the
              forecast reaches a phone at all.

              Removes itself for a day it has no forecast for, which is every
              day of the week until around 21 October: Open-Meteo reaches
              sixteen days ahead and the welcome dinner was twenty-two away
              when this was built. So the strips arrive one day at a time from
              the top of the week down, and until then this page is
              unchanged. */}
          {!desktop && (
            <WeatherHours hours={weather?.hours ?? []} events={day.events} dayKey={day.key} />
          )}
          <div className="ev-list">
            {day.events.map((event) => {
              const state = model.stateOf(event);
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  venue={model.venueOf(event.venueId)}
                  state={state}
                  at={model.at}
                  density={state === 'now' || state === 'next' ? 'hero' : 'row'}
                />
              );
            })}
          </div>
        </section>
      ))}
      {/* The weather credit used to ride here. It is a licence condition
          (CC BY 4.0) and now sits in the site footer, on every screen. */}
      <div className="tz-note">All times Khao Lak, GMT+7</div>
    </>
  );

  if (desktop) {
    return (
      <div className="cols">
        <div>
          <OfflinePill fetchedAt={model.fetchedAt} />
          {days}
        </div>
        <aside className="col-side">
          {/* Above the wardrobe planner, because the two answer the same
              question in order: what will it be doing, and therefore what
              should I wear. Renders nothing while the forecast does not reach
              the week, leaving the rail exactly as it was. */}
          <WeatherRail hours={weather?.hours ?? []} days={model.days} at={model.at} />
          {model.wardrobe && <Wardrobe content={model.wardrobe} full />}
        </aside>
      </div>
    );
  }

  return (
    <div className="page">
      <OfflinePill fetchedAt={model.fetchedAt} />
      {model.wardrobe && (
        <div style={{ padding: '12px 14px 0' }}>
          <Wardrobe content={model.wardrobe} />
        </div>
      )}
      {days}
    </div>
  );
}
